import React from 'react';
import axios from 'axios';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import clientConfig from 'clientConfig';
import { consoleLogger } from 'helpers';
import { Auth } from 'aws-amplify';
import { useForm } from 'react-hook-form';
import { Form, Button, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setMessage, clearMessage } from 'actions/messageAction';
import { uploadProgramFile } from 'actions/programFileAction';
import { setLoading, clearLoading } from 'actions/loadingAction';
import { clearProgressRate } from 'actions/progressRateAction';
import { lang } from 'lang';
import UnitLoading from 'components/parts/loading/overlay';
import confirm from 'components/parts/item/confirmationModal';


/**
 * プログラムファイルアップロードコンポーネント
 */
const UploadFile = () => {
  const dispatch = useDispatch();
  const { error, msg, validate } = lang;
  const [ filenames, setFileNames ] = React.useState({
    file1: msg.update_file.no_select_file,
    file2: msg.update_file.no_select_file,
  });
  const [ isEmptyUpdateInfo, setUpdateInfoFlag ] = React.useState(false); // true:アップデート情報が空, false:アップデート情報が空でない
  const { errors, formState, register, reset, handleSubmit, setValue, setError, clearErrors } = useForm({mode: 'onChange'});
  const fileState = useSelector((state) => state.programFile);
  // true:アップデート情報のテキスト入力無効, false:有効
  const disableUpdateInfo = isEmptyUpdateInfo || filenames.file1 === msg.update_file.no_select_file || errors.file1 !== undefined;
  // true:Uploadボタン無効, false:有効
  const disableSubmit = disableUpdateInfo || formState.isSubmitting || filenames.file2 === msg.update_file.no_select_file || errors.file2 !== undefined || errors.update_info !== undefined;

  /**
   * アップロードファイルを検証する
   * @param {object}   file        ファイルオブジェクト
   * @param {boolean} isSecondFile ファイル2:true, ファイル1:false
   * @returns 
   */
  const validateFile = (file, isSecondFile = false) => {
    // メッセージの初期化
    dispatch(clearMessage());

    // ファイルが選択されてない場合、エラーメッセージを表示する
    if (file.length === 0) {
      dispatch(setMessage('warning', validate.required));
      return false;
    }

    // ファイル名が半角英数とピリオド(.) ダッシュ(-) 下線(_)以外の文字を使用している場合、エラーメッセージを表示する
    if (!file[0].name.match(/^[0-9a-zA-Z._-]*$/)) {
      dispatch(setMessage('warning', validate.filename_pattern_2));
      return false;
    }

    const nameArr = file[0].name.split('_');
    //ファイル名に_が1つも含まれない
    if (nameArr.length === 0 ) {
      dispatch(setMessage('warning', validate.file_format1 ));
      return false;
    } else {
      // ファイル2のバリデーションチェック
      if (isSecondFile) {
        // ファイル1とファイル2のファイル名_バージョンの部分が一致しない場合、エラーメッセージを表示する
        if (filenames.file1 !== msg.update_file.no_select_file && filenames.file1 + '_2' !== file[0].name) {
          dispatch(setMessage('warning', validate.file_format2));
          return false;
        } else {
          return true;
        }        
      // ファイル1のバリデーションチェック
      } else {
        // 「ファイル名_バージョン」の形式であるかをチェックする
        if (nameArr.length === 2 && nameArr[0].length >= 1 && nameArr[1].length >= 1) {
          // ファイル1とファイル2のファイル名_バージョンの部分が一致しない場合、エラーメッセージを表示する
          if (filenames.file2 !== msg.update_file.no_select_file && filenames.file2 !== file[0].name + '_2') {
            // ファイル1のエラーではなく、ファイル2のエラーとして処理する
            setError('file2', {type: 'validate', message: ''});
            dispatch(setMessage('warning', validate.file_format2));
            return true;
          } else {
            // ファイル1 = ファイル2 且つ ファイル2のエラーがデータとして残っていいる場合、クリアにする
            if (filenames.file2 === file[0].name + '_2' && errors.file2 !== undefined) {
              clearErrors('file2');
            }
            return true;
          }
        } else {
          dispatch(setMessage('warning', validate.file_format1 ));
          return false;
        }
      }
    }
  };

  /**
   * 入力されたアップデート情報の値を検証する
   * @param {string} update_info アップデート情報
   * @returns 
   */
  const validateUpdateInfo = update_info => {
    // メッセージの初期化
    dispatch(clearMessage());

    // アップデート情報が空の場合は、エラーメッセージを表示する
    if (update_info === undefined || update_info === '') {
      dispatch(setMessage('warning', validate.required));
      return false;
    }

    // アップデート情報が2000文字超の場合は、エラーメッセージを表示する
    if (update_info.length > 2000) {
      dispatch(setMessage('warning', validate.max_length.replace('%s', 2000)));
      return false;
    } 
    return true;
  };
  
  /**
   * Uploadボタンを押下したときに実行するSubmit処理
   * @param {object} params 
   */
  const onSubmit = async params => {
    let isUpload        = false; // アップロード処理実行フラグ (true:実行する/ false:実行しない)
    const isUpdateFile  = { file1: false, file2: false };
    const list1 = fileState.items.filter(item => item.key === params.file1[0].name);
    const list2 = fileState.items.filter(item => item.key === params.file2[0].name);

    // 既にファイルが存在する場合は、確認ポップアップを表示する
    if (list1.length > 0 || list2.length > 0) {
      const isUpdate = await confirm({
        body: msg.update_file.confirm_update,
      });
      isUpload = isUpdate ? true : false;

      // ファイル1が既に存在する場合は、ファイル1の更新フラグをtrueにセット
      if (list1.length > 0) {
        isUpdateFile.file1 = true;
      }
      // ファイル2が既に存在する場合は、ファイル2の更新フラグをtrueにセット
      if (list2.length > 0) {
        isUpdateFile.file2 = true;
      }
      setValue('update_info', params.update_info);
    } else {
      isUpload = true;
    }
    // アップロード実行の場合
    if (isUpload) {
      // ローディング開始
      dispatch(setLoading('overlay'));

      await dispatch(
        // プログラムファイル1をアップロードする
        await uploadProgramFile(params.file1[0], isUpdateFile.file1, false)
      ).then(async (result) => {
        // Success
        if (result.status === 200) {
          await dispatch(
            // プログラムファイル2をアップロードする
            await uploadProgramFile(params.file2[0], isUpdateFile.file2, true)
          ).then(async (result) => {
            // Success
            if (result.status === 200) {
              const currentSession = await Auth.currentSession();
              const url = clientConfig['api_url'] + '/srv/versions/' + params.file1[0].name;
              const requestData = {
                method: 'put',
                url: url,
                headers: { 'Authorization': currentSession.idToken.jwtToken },
                data: {
                  update_info: params.update_info
                }
              };
              await axios(requestData)
                .then(res => {
                  consoleLogger('API PUT:' + url);
                  consoleLogger(res);
                  // Success
                  if (res.status === 200) {
                    dispatch(setMessage('success', msg.update_file.upload));
                  // Error                      
                  } else {
                    dispatch(setMessage('danger', error.update_file.update + `\n${res.data.errcode}`));      
                  }
                }).catch(err => {
                  consoleLogger(err.response);
                  if (err.response === undefined ) {
                    dispatch(setMessage('danger', error.update_file.update + `\n${err.message}`));
                  } else {
                    dispatch(setMessage('danger', error.update_file.update + `\n${err.response.data.errcode}`));
                  }
                });
            } else {
              dispatch(setMessage('danger', error.update_file.upload + `\n${result.errcode}`));
            }
          });
        // Error
        } else {
          dispatch(setMessage('danger', error.update_file.upload + `\n${result.errcode}`));
        }
      });
      // ローディングクリア
      dispatch(clearProgressRate());
      dispatch(clearLoading('overlay'));

      // フォームリセット
      reset();
      setFileNames({
        file1: msg.update_file.no_select_file,
        file2: msg.update_file.no_select_file,
      });  
    }
  };

  /**
   * ファイル選択ボタンをレンダリングする
   * @param {number} key 1:Choose File1, 2:Choose File2
   * @returns 
   */
  const renderUploadFile = (key) => {
    const isSeccondFile = key === 2 ? true : false;
    let disableInput = false; //デフォルトは有効化
    // ファイル2の場合
    if (isSeccondFile) {
      // ファイル1未選択の場合は、ファイル2を無効化
      if (filenames.file1 == msg.update_file.no_select_file) {
        disableInput = true;
      // ファイル1がバリデーションエラーの場合は、ファイル2を無効化
      } else if (errors.file1 !== undefined) {
        disableInput = true;
      } else {
        disableInput = false;
      }
    }

    /**
     * アップデート情報の入力値をセットする
     * @param {string} value アップデート情報
     */
    const setUpdateInfoValue = (value) => {
      setValue('update_info', value); // Update info value clear
    };

    /**
     * サーバーからアップデート情報を取得し、input valueにセットする
     * @param {object} fileList  ファイルオブジェクト
     */
    const setUpdateInfo = async (fileList) => {
      if (fileList.length === 1 && errors.file === undefined) {
        const currentSession = await Auth.currentSession();
        const url = clientConfig['api_url'] + '/srv/versions/' + fileList[0].name;
        const requestData = {
          method: 'get',
          url: url,
          headers: { 'Authorization': currentSession.idToken.jwtToken },
        };
        setUpdateInfoFlag(true);
        // 選択したファイルのバージョン情報が存在するかをチェックする
        await axios(requestData)
          .then(res => {
            consoleLogger('API GET:' + url);
            consoleLogger(res);
            // ファイル1がバリデーションエラーの場合は、空文字をセットする
            if (errors.file1 !== undefined) {
              setUpdateInfoValue('');
            } else {
              setUpdateInfoValue(res.data.Item.version.update_info);
            }
          }).catch(err => {
            consoleLogger(err);
            setUpdateInfoValue('');
          });
        setUpdateInfoFlag(false);
      }
    };

    /**
     * 選択したファイルの名前を保持する
     * @param {string} key       inputのnameキー
     * @param {object} fileList  ファイルオブジェクト
     */
    const setFileName = (key, fileList) => {
      if (fileList.length === 1) {
        setFileNames({
          ...filenames,
          [key]: fileList[0].name
        });
      } else {
        setFileNames({
          ...filenames,
          [key]: msg.update_file.no_select_file
        });  
      }
    };

    /**
     * ファイルをドロップしたときにコールされる関数
     * @param {object} e イベントオブジェクト
     */
    const onDrop = async (e) => {
      e.preventDefault();
      // ファイル2の場合
      if (isSeccondFile) {
        // バリデーションエラーの場合
        const isValid = validateFile(e.dataTransfer.files, true);
        if (!isValid) {
          setError('file2', {type: 'validate', message: ''}); // registerを使用してないため、バリデーションのエラーは手動でセットしなければならない
        } else {
          clearErrors('file2');                               // registerを使用してないため、バリデーションのエラーの解除は手動でセットしなければならない
        }
        setFileName('file2',e.dataTransfer.files);
        setValue('file2',e.dataTransfer.files);               // name = file2のinput valueにファイル情報を手動でセット
      } else {
        const isValid = validateFile(e.dataTransfer.files);
        // バリデーションエラーの場合
        if (!isValid) {
          setError('file1', {type: 'validate', message: ''}); // registerを使用してないため、バリデーションのエラーは手動でセットしなければならない
          // ファイル1選択
          if (e.dataTransfer.files.length !== 0) {
            setFileNames({
              file1: e.dataTransfer.files[0].name,
              file2: msg.update_file.no_select_file, 
            });
          }
          setUpdateInfoValue('');
        } else {
          clearErrors('file1');                               // registerを使用してないため、バリデーションのエラーの解除は手動でセットしなければならない
          setFileName('file1',e.dataTransfer.files);
          setValue('file1',e.dataTransfer.files);             // name = file1のinput valueにファイル情報を手動でセット
          await setUpdateInfo(e.dataTransfer.files);
        }
      }
    };

    /**
     * ファイルを変更したときにコールされる関数
     * @param {object} e イベントオブジェクト
     */
    const onChange = async (e) => {
      // ファイル2の場合
      if (isSeccondFile) {
        setFileName('file2',e.target.files);
      } else {
        const isValid = errors.file1 === undefined;
        // バリデーションエラーの場合
        if (!isValid) {
          // ファイル1選択
          if (e.target.files.length !== 0) {
            setFileNames({
              file1: e.target.files[0].name,
              file2: msg.update_file.no_select_file, 
            });
            setValue('file2',null);
          // ファイル1未選択
          } else {
            setFileNames({
              file1: msg.update_file.no_select_file,
              file2: msg.update_file.no_select_file, 
            });
            setValue('file2', null);
          }
          setUpdateInfoValue('');
        } else {
          setFileName('file1',e.target.files);
          await setUpdateInfo(e.target.files);
        }
      }
    };

    return (
      <Div>
        <div>
          <FileLabel
            onDragOver={(e) => e.preventDefault()}
            onDrop={async (e) => {await onDrop(e);}}
            style={{opacity: disableInput || formState.isSubmitting ? '0.6' : '1'}}
          >
            {`Choose File${key}`}
            <input
              id={`upload-file${key}`}
              style={{display: 'none'}}
              type='file'
              name={`file${key}`}
              ref={
                register({
                  validate: (e) => validateFile(e, isSeccondFile)
                })
              }
              onChange={async (e) => {await onChange(e);}}
              disabled={disableInput || formState.isSubmitting}
            />
          </FileLabel>
          <Span>{filenames[`file${key}`]}</Span>
        </div>
      </Div>
    );
  };

  const renderUploadFiles = () => {
    return (
      <>
        {[1,2].map(key => (
          <th key={key}>
            {renderUploadFile(key)}
          </th>
        ))}
      </>
    );
  };

  return (
    <FormUp onSubmit={handleSubmit(onSubmit)}>
      <UnitLoading/>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{maxWidth: 150, width: 150, minWidth: 150}}>
              <label data-testid='upload-file'>Upload file</label>
            </th>
            {renderUploadFiles()}
            <th style={{maxWidth: 146, width: 146, minWidth: 146, textAlign: 'right'}}>
              <DivUp>
                <UploadButton
                  className='btn btn-primary'
                  type='submit'
                  disabled={disableSubmit}
                >Upload</UploadButton>
              </DivUp>
            </th>
          </tr>
          <tr>
            <ThLabel>
              <label htmlFor='update-info'>Update info</label>
            </ThLabel>
            <th colSpan={3}>
              <Div>
                <Form.Control as='textarea'
                  id='update-info'
                  name='update_info'
                  maxLength={2000}
                  ref={
                    register({
                      validate: validateUpdateInfo
                    })
                  }
                  className={errors.update_info ? 'input-warning' : ''}
                  rows={3}
                  disabled={disableUpdateInfo}
                />
              </Div>
            </th> 
          </tr>
        </thead>
      </Table>
    </FormUp>
  );
};
UploadFile.propTypes = {
  history: PropTypes.object
};
const FormUp = styled(Form)`
  margin-bottom: 15px;
`;
const Div = styled.div`
  display: flex;
`;
const DivUp = styled.div`
  margin-left: auto;
`;
const UploadButton = styled(Button)`
  width: 120px;
  max-width:120px;
  max-width:120px;
`;
const ThLabel = styled.th`
  vertical-align: middle !important;
`;
const FileLabel = styled.label`
  background-color: #EEEEEE;
  width: 118px;
  height: 30px;
  padding-left: 14px;
  padding-top: 2px;
  margin-right: 12px;
  border: 1px solid gray;
  font-weight: normal;
  border-radius: 3px;
`;
const Span = styled.span`
  font-weight: normal;
`;
export default UploadFile;
